/**
 * LMIV-Allergene + Diät-Flags — EINE gemeinsame Quelle für alle Anzeige-Flächen
 * (Katalog, Menü-Import, Pakete, Angebots-Builder, öffentliches Angebot).
 *
 * Rechtlicher Rahmen (LMIV / § 4 LMIDV): Allergen-Information muss überall dort verfügbar
 * sein, wo Speisen angeboten werden. Deshalb gilt UI-weit die Tri-State-Semantik aus dem
 * Katalog: `null` = ungepflegt (sichtbar als „–", NIE als „keine" darstellen!),
 * `[]` = geprüft: keine Hauptallergene, Codes = geprüfte Allergene.
 */

/** 14 LMIV-Hauptallergene (Buchstaben-Kennzeichnung wie in DE üblich), DE + EN Label. */
export const LMIV: { code: string; label: string; labelEn: string }[] = [
  { code: "A", label: "Glutenhaltiges Getreide", labelEn: "Cereals containing gluten" },
  { code: "B", label: "Krebstiere", labelEn: "Crustaceans" },
  { code: "C", label: "Eier", labelEn: "Eggs" },
  { code: "D", label: "Fisch", labelEn: "Fish" },
  { code: "E", label: "Erdnüsse", labelEn: "Peanuts" },
  { code: "F", label: "Soja", labelEn: "Soybeans" },
  { code: "G", label: "Milch / Laktose", labelEn: "Milk / lactose" },
  { code: "H", label: "Schalenfrüchte", labelEn: "Tree nuts" },
  { code: "L", label: "Sellerie", labelEn: "Celery" },
  { code: "M", label: "Senf", labelEn: "Mustard" },
  { code: "N", label: "Sesam", labelEn: "Sesame" },
  { code: "O", label: "Schwefeldioxid / Sulfite", labelEn: "Sulphur dioxide / sulphites" },
  { code: "P", label: "Lupinen", labelEn: "Lupin" },
  { code: "R", label: "Weichtiere", labelEn: "Molluscs" },
];

const LMIV_BY_CODE = new Map(LMIV.map((l) => [l.code, l]));

/** Voller Name zu einem Code (Fallback: der Code selbst, falls unbekannte Alt-Daten). */
export function lmivLabel(code: string, lang: "de" | "en" = "de"): string {
  const entry = LMIV_BY_CODE.get(code);
  if (!entry) return code;
  return lang === "en" ? entry.labelEn : entry.label;
}

/** Diät-/Unverträglichkeits-Flags (identisch zur Katalog-Pflege). */
export const DIETARY: { key: string; label: string; labelEn: string; short: string }[] = [
  { key: "vegetarian", label: "Vegetarisch", labelEn: "Vegetarian", short: "Veg" },
  { key: "vegan", label: "Vegan", labelEn: "Vegan", short: "Vegan" },
  { key: "gluten_free", label: "Glutenfrei", labelEn: "Gluten-free", short: "GF" },
  { key: "lactose_free", label: "Laktosefrei", labelEn: "Lactose-free", short: "LF" },
];

/** Aktive Diät-Flags eines Items als Liste (stabile Reihenfolge wie DIETARY). */
export function activeDietary(dietary: Record<string, boolean> | null | undefined): typeof DIETARY {
  if (!dietary) return [];
  return DIETARY.filter((d) => dietary[d.key] === true);
}

/**
 * Allergen-Codes + Diät-Flags als kompakte, read-only Badge-Zeile.
 * Tri-State: `allergens === null` -> „–" (ungepflegt, Tooltip erklärt), `[]` -> „keine",
 * Codes -> Buchstaben-Chips mit vollem Namen im Tooltip.
 */
export function AllergenBadges({
  allergens,
  dietary,
  className = "",
}: {
  allergens: string[] | null | undefined;
  dietary?: Record<string, boolean> | null;
  className?: string;
}) {
  const diet = activeDietary(dietary);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {allergens == null ? (
        <span
          className="font-body-sm text-body-sm text-on-surface-variant/50"
          title="Allergene ungepflegt – bitte im Katalog prüfen (LMIV)"
        >
          –
        </span>
      ) : allergens.length === 0 ? (
        <span className="font-body-sm text-body-sm text-green-700 dark:text-green-300" title="Geprüft: keine LMIV-Hauptallergene">
          keine
        </span>
      ) : (
        [...allergens].sort().map((a) => (
          <span
            key={a}
            className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-surface-container-high px-1 font-label-md text-[11px] font-semibold text-on-surface-variant dark:bg-surface-variant/30"
            title={lmivLabel(a)}
          >
            {a}
          </span>
        ))
      )}
      {diet.map((d) => (
        <span
          key={d.key}
          className="inline-flex h-5 items-center justify-center rounded bg-primary/10 px-1.5 font-label-md text-[11px] font-semibold text-primary dark:bg-primary-fixed-dim/15 dark:text-primary-fixed-dim"
          title={d.label}
        >
          {d.short}
        </span>
      ))}
    </span>
  );
}

/**
 * Union der Allergene über mehrere Positionen (z. B. alle Gänge eines Menü-Pakets).
 * LMIV-Kernregel: Schon EINE ungepflegte Position (`null`) macht das Aggregat unvollständig —
 * dann darf NIE „keine Allergene" behauptet werden (`resolved: false`).
 */
export function aggregateAllergens(
  items: { allergens: string[] | null | undefined }[],
): { union: string[]; resolved: boolean } {
  const set = new Set<string>();
  let resolved = true;
  for (const it of items) {
    if (it.allergens == null) {
      resolved = false;
      continue;
    }
    for (const a of it.allergens) set.add(a);
  }
  return { union: [...set].sort(), resolved };
}
